import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTranslation } from 'react-i18next';

// Get features data with translations
const getFeatures = (t) => {
  return [
    {
      id: 'error-handling',
      title: t('features.featureList.errorHandling.title'),
      description: t('features.featureList.errorHandling.description'),
      shortBenefits: t('features.featureList.errorHandling.shortBenefits', { returnObjects: true }),
      longDescription: t('features.featureList.errorHandling.longDescription'),
      context: t('features.featureList.errorHandling.context'),
      example: t('features.featureList.errorHandling.example'),
      icon: (props) => (
        <svg
          {...props}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      )
    },
    {
      id: 'journal-system',
      title: t('features.featureList.journalSystem.title'),
      description: t('features.featureList.journalSystem.description'),
      shortBenefits: t('features.featureList.journalSystem.shortBenefits', { returnObjects: true }),
      longDescription: t('features.featureList.journalSystem.longDescription'),
      context: t('features.featureList.journalSystem.context'),
      example: t('features.featureList.journalSystem.example'),
      icon: (props) => (
        <svg
          {...props}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
      )
    },
    {
      id: 'type-conversion',
      title: t('features.featureList.typeConversion.title'),
      description: t('features.featureList.typeConversion.description'),
      shortBenefits: t('features.featureList.typeConversion.shortBenefits', { returnObjects: true }),
      longDescription: t('features.featureList.typeConversion.longDescription'),
      context: t('features.featureList.typeConversion.context'),
      example: t('features.featureList.typeConversion.example'),
      icon: (props) => (
        <svg
          {...props}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
          />
        </svg>
      )
    },
    {
      id: 'multi-db',
      title: t('features.featureList.multiDb.title'),
      description: t('features.featureList.multiDb.description'),
      shortBenefits: t('features.featureList.multiDb.shortBenefits', { returnObjects: true }),
      longDescription: t('features.featureList.multiDb.longDescription'),
      context: t('features.featureList.multiDb.context'),
      example: t('features.featureList.multiDb.example'),
      icon: (props) => (
        <svg
          {...props}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
          />
        </svg>
      )
    },
    {
      id: 'versioned-migrations',
      title: t('features.featureList.versionedMigrations.title'),
      description: t('features.featureList.versionedMigrations.description'),
      shortBenefits: t('features.featureList.versionedMigrations.shortBenefits', { returnObjects: true }),
      longDescription: t('features.featureList.versionedMigrations.longDescription'),
      context: t('features.featureList.versionedMigrations.context'),
      example: t('features.featureList.versionedMigrations.example'),
      icon: (props) => (
        <svg
          {...props}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      )
    },
    {
      id: 'tags',
      title: t('features.featureList.tags.title'),
      description: t('features.featureList.tags.description'),
      shortBenefits: t('features.featureList.tags.shortBenefits', { returnObjects: true }),
      longDescription: t('features.featureList.tags.longDescription'),
      context: t('features.featureList.tags.context'),
      example: t('features.featureList.tags.example'),
      icon: (props) => (
        <svg
          {...props}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          />
        </svg>
      )
    },
    {
      id: 'sql-analysis',
      title: t('features.featureList.sqlAnalysis.title'),
      description: t('features.featureList.sqlAnalysis.description'),
      shortBenefits: t('features.featureList.sqlAnalysis.shortBenefits', { returnObjects: true }),
      longDescription: t('features.featureList.sqlAnalysis.longDescription'),
      context: t('features.featureList.sqlAnalysis.context'),
      example: t('features.featureList.sqlAnalysis.example'),
      icon: (props) => (
        <svg
          {...props}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
          />
        </svg>
      )
    },
    {
      id: 'logging',
      title: t('features.featureList.logging.title'),
      description: t('features.featureList.logging.description'),
      shortBenefits: t('features.featureList.logging.shortBenefits', { returnObjects: true }),
      longDescription: t('features.featureList.logging.longDescription'),
      context: t('features.featureList.logging.context'),
      example: t('features.featureList.logging.example'),
      icon: (props) => (
        <svg
          {...props}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      )
    },
    {
      id: 'multi-commands',
      title: t('features.featureList.multiCommands.title'),
      description: t('features.featureList.multiCommands.description'),
      shortBenefits: t('features.featureList.multiCommands.shortBenefits', { returnObjects: true }),
      longDescription: t('features.featureList.multiCommands.longDescription'),
      context: t('features.featureList.multiCommands.context'),
      example: t('features.featureList.multiCommands.example'),
      icon: (props) => (
        <svg
          {...props}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      )
    }
  ];
};

const FeatureDetail = ({ feature }) => {
  const { t } = useTranslation();
  
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <h4 className="text-xl font-semibold text-dblift-blue-dark mb-3">{t('features.featureList.detail.detailedDesc')}</h4>
          <p className="text-dblift-gray-dark">{feature.longDescription}</p>
        </div>
        <div>
          <h4 className="text-xl font-semibold text-dblift-blue-dark mb-3">{t('features.featureList.detail.usageContext')}</h4>
          <p className="text-dblift-gray-dark">{feature.context}</p>
        </div>
      </div>
      
      <div>
        <h4 className="text-xl font-semibold text-dblift-blue-dark mb-3">{t('features.featureList.detail.usageExample')}</h4>
        <SyntaxHighlighter 
          language="bash" 
          style={atomDark} 
          className="rounded-lg text-sm md:text-base"
        >
          {feature.example}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};

const FeaturesPage = () => {
  const [expandedFeatures, setExpandedFeatures] = useState([]);
  const { t } = useTranslation();
  const features = getFeatures(t);

  // Toggle feature expansion inline with the card
  const toggleFeatureExpansion = (featureId) => {
    if (expandedFeatures.includes(featureId)) {
      setExpandedFeatures(expandedFeatures.filter(id => id !== featureId));
    } else {
      setExpandedFeatures([...expandedFeatures, featureId]);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-dblift-blue-dark to-dblift-blue-light py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              dangerouslySetInnerHTML={{ __html: t('features.hero.title') }}
            >
            </motion.h1>
            <motion.p 
              className="text-xl text-white opacity-90 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {t('features.hero.description')}
            </motion.p>
          </div>
        </div>
      </div>

      {/* Features Display */}
      <div className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold text-dblift-blue-dark mb-4">
              {t('features.main.title')}
            </h2>
            <p className="text-lg text-dblift-gray-dark">
              {t('features.main.description')}
            </p>
          </div>

          {/* Technical Advantages */}
          <div className="mb-20 bg-dblift-gray-light rounded-xl p-8">
            <h3 className="text-2xl font-bold text-dblift-blue-dark mb-6 text-center">
              {t('features.techSection.title')}
            </h3>
            <p className="text-lg text-center mb-10 max-w-3xl mx-auto">
              {t('features.techSection.description')}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {t('features.techSection.items', { returnObjects: true }).map((item, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300">
                  <h4 className="text-xl font-semibold text-dblift-blue-dark mb-3">{item.title}</h4>
                  <p className="text-dblift-gray-dark">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8">
            {features.map((feature) => (
              <div key={feature.id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                {/* Feature Header */}
                <div className="p-6 cursor-pointer hover:bg-dblift-gray-light rounded-t-xl transition-colors duration-200" onClick={() => toggleFeatureExpansion(feature.id)}>
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 h-12 w-12 bg-dblift-orange rounded-lg flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="ml-4 text-xl font-bold text-dblift-blue-dark">{feature.title}</h3>
                    <div className="ml-auto">
                      <span className={`transition-transform duration-300 block ${
                        expandedFeatures.includes(feature.id) ? 'rotate-180' : ''
                      }`}>
                        <svg className="h-6 w-6 text-dblift-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <p className="text-dblift-gray-dark mb-4">{feature.description}</p>
                  <ul className="space-y-2 text-dblift-gray-dark">
                    {feature.shortBenefits.map((benefit, i) => (
                      <li key={i} className="flex items-start">
                        <svg className="h-5 w-5 text-dblift-orange mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Feature Details */}
                <AnimatePresence>
                  {expandedFeatures.includes(feature.id) && (
                    <motion.div 
                      key={`detail-${feature.id}`}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-6 pb-6 overflow-hidden border-t border-gray-200"
                    >
                      <div className="pt-6">
                        <FeatureDetail feature={feature} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonial/Call to Action */}
      <div className="bg-dblift-gray-light py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 
                className="text-3xl font-bold text-dblift-blue-dark mb-4"
                dangerouslySetInnerHTML={{ __html: t('features.cta.title') }}
              >
              </h2>
              <p className="text-lg text-dblift-gray-dark">
                {t('features.cta.description')}
              </p>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
              <Link to="/download" className="btn btn-primary w-full md:w-auto">
                {t('features.cta.downloadBtn')}
              </Link>
              <Link to="/docs/quick-start" className="btn btn-outline w-full md:w-auto">
                {t('features.cta.startGuideBtn')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturesPage;